/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getPregnancyCases } from 'app/entities/pregnancy-case/pregnancy-case.reducer';
import { getEntities as getDoctors } from 'app/entities/doctor/doctor.reducer';
import { getEntities as getHospitals } from 'app/entities/hospital/hospital.reducer';
import { getEntities as getVitals } from 'app/entities/vitals/vitals.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { Gestation } from 'app/shared/model/enumerations/gestation.model';
import { DeliveryType } from 'app/shared/model/enumerations/delivery-type.model';
import { createEntity, getEntity, reset, updateEntity } from './birth-history.reducer';

export const BirthHistoryUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const pregnancyCases = useAppSelector(state => state.pregnancyCase.entities);
  const doctors = useAppSelector(state => state.doctor.entities);
  const hospitals = useAppSelector(state => state.hospital.entities);
  const vitals = useAppSelector(state => state.vitals.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const birthHistoryEntity = useAppSelector(state => state.birthHistory.entity);
  const loading = useAppSelector(state => state.birthHistory.loading);
  const updating = useAppSelector(state => state.birthHistory.updating);
  const updateSuccess = useAppSelector(state => state.birthHistory.updateSuccess);
  const gestationValues = Object.keys(Gestation);
  const deliveryTypeValues = Object.keys(DeliveryType);

  const handleClose = () => {
    navigate(`/birth-history${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPregnancyCases({}));
    dispatch(getDoctors({}));
    dispatch(getHospitals({}));
    dispatch(getVitals({}));
    dispatch(getGlucoUsers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.birthWeight !== undefined && typeof values.birthWeight !== 'number') {
      values.birthWeight = Number(values.birthWeight);
    }
    if (values.birthHeight !== undefined && typeof values.birthHeight !== 'number') {
      values.birthHeight = Number(values.birthHeight);
    }
    if (values.birthOrder !== undefined && typeof values.birthOrder !== 'number') {
      values.birthOrder = Number(values.birthOrder);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...birthHistoryEntity,
      ...values,
      pregnancy: pregnancyCases.find(it => it.id.toString() === values.pregnancy?.toString()),
      recordedBy: doctors.find(it => it.id.toString() === values.recordedBy?.toString()),
      recordedAt: hospitals.find(it => it.id.toString() === values.recordedAt?.toString()),
      vitals: vitals.find(it => it.id.toString() === values.vitals?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
      mother: glucoUsers.find(it => it.id.toString() === values.mother?.toString()),
      father: glucoUsers.find(it => it.id.toString() === values.father?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          gestation: 'FULL',
          delivery: 'NORMAL',
          ...birthHistoryEntity,
          pregnancy: birthHistoryEntity?.pregnancy?.id,
          recordedBy: birthHistoryEntity?.recordedBy?.id,
          recordedAt: birthHistoryEntity?.recordedAt?.id,
          vitals: birthHistoryEntity?.vitals?.id,
          patient: birthHistoryEntity?.patient?.id,
          mother: birthHistoryEntity?.mother?.id,
          father: birthHistoryEntity?.father?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.birthHistory.home.createOrEditLabel" data-cy="BirthHistoryCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.birthHistory.home.createOrEditLabel">Create or edit a BirthHistory</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="birth-history-id"
                  label={translate('tinyMedicRestApp.birthHistory.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.gestation')}
                id="birth-history-gestation"
                name="gestation"
                data-cy="gestation"
                type="select"
              >
                {gestationValues.map(gestation => (
                  <option value={gestation} key={gestation}>
                    {translate(`tinyMedicRestApp.Gestation.${gestation}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.delivery')}
                id="birth-history-delivery"
                name="delivery"
                data-cy="delivery"
                type="select"
              >
                {deliveryTypeValues.map(deliveryType => (
                  <option value={deliveryType} key={deliveryType}>
                    {translate(`tinyMedicRestApp.DeliveryType.${deliveryType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.birthWeight')}
                id="birth-history-birthWeight"
                name="birthWeight"
                data-cy="birthWeight"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.birthHeight')}
                id="birth-history-birthHeight"
                name="birthHeight"
                data-cy="birthHeight"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.breastFed')}
                id="birth-history-breastFed"
                name="breastFed"
                data-cy="breastFed"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.twins')}
                id="birth-history-twins"
                name="twins"
                data-cy="twins"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.birthOrder')}
                id="birth-history-birthOrder"
                name="birthOrder"
                data-cy="birthOrder"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.notes')}
                id="birth-history-notes"
                name="notes"
                data-cy="notes"
                type="text"
                validate={{
                  maxLength: { value: 250, message: translate('entity.validation.maxlength', { max: 250 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.active')}
                id="birth-history-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.draft')}
                id="birth-history-draft"
                name="draft"
                data-cy="draft"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.cursor')}
                id="birth-history-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.lastUpdated')}
                id="birth-history-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.microSec')}
                id="birth-history-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.birthHistory.timestampEntry')}
                id="birth-history-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="birth-history-pregnancy"
                name="pregnancy"
                data-cy="pregnancy"
                label={translate('tinyMedicRestApp.birthHistory.pregnancy')}
                type="select"
              >
                <option value="" key="0" />
                {pregnancyCases
                  ? pregnancyCases.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="birth-history-recordedBy"
                name="recordedBy"
                data-cy="recordedBy"
                label={translate('tinyMedicRestApp.birthHistory.recordedBy')}
                type="select"
              >
                <option value="" key="0" />
                {doctors
                  ? doctors.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="birth-history-recordedAt"
                name="recordedAt"
                data-cy="recordedAt"
                label={translate('tinyMedicRestApp.birthHistory.recordedAt')}
                type="select"
              >
                <option value="" key="0" />
                {hospitals
                  ? hospitals.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="birth-history-vitals"
                name="vitals"
                data-cy="vitals"
                label={translate('tinyMedicRestApp.birthHistory.vitals')}
                type="select"
              >
                <option value="" key="0" />
                {vitals
                  ? vitals.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="birth-history-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.birthHistory.patient')}
                type="select"
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="birth-history-mother"
                name="mother"
                data-cy="mother"
                label={translate('tinyMedicRestApp.birthHistory.mother')}
                type="select"
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="birth-history-father"
                name="father"
                data-cy="father"
                label={translate('tinyMedicRestApp.birthHistory.father')}
                type="select"
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/birth-history" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default BirthHistoryUpdate;
